import {IOrderRuleComponentProps, IOrderRuleForm} from "../../../../../../store/trafficManagement/orderRules/types";
import Select from "../../../../../../components/Common/Select";
import {useFormikContext} from "formik";
import {useQuery} from "@tanstack/react-query";
import * as Yup from 'yup';
import React from "react";
import {Label} from "reactstrap";
import {CountryBoxService} from "../../../../../../services/country_box.service";
import {UserService} from "../../../../../../services/user.service";

export const defaultRulesAutoAssign = {
    countries: {type: "array", value: [] as { id: number }[]},
    users: {type: "array", value: [] as { id: number }[]},
};

export const yupRulesAutoAssignSchema = Yup.object().shape({
    countries: Yup.object().shape({
        type: Yup.string().oneOf(["array"]).required(),
        value: Yup.array().of(Yup.object({
            id: Yup.number().required(),
        })).min(1, "At least one country required")
    }).required(),

    users: Yup.object().shape({
        type: Yup.string().oneOf(["array"]).required(),
        value: Yup.array().of(Yup.object({
            id: Yup.number().required(),
        })).min(1, "At least one user required")
    }).required()
});

const AutoAssignOrderRule: React.FC<IOrderRuleComponentProps> = ({rulesPath, isPendingSave}) => {
    const formik = useFormikContext<IOrderRuleForm>();
    const {values, errors, touched, setFieldValue, handleBlur} = formik;

    const rule = (values.rule ?? defaultRulesAutoAssign) as typeof defaultRulesAutoAssign;

    const {data: usersSelect, isLoading: isLoadingUsers} = useQuery({
        queryKey: ["keyUsersBuyerTeamList"],
        queryFn: UserService.getUsersBuyerTeamLead
    });

    const selectCountries = (countries: any) =>
        countries.data.map((cb: any) => ({value: cb.id, label: cb.title}))

    const {data: countriesSelect, isLoading: isLoadingCountries} = useQuery({
        queryKey: ["keyCountries"], queryFn: CountryBoxService.getCountries,
        select: selectCountries
    })

    const selectedCountries = countriesSelect?.filter((country: any) => rule.countries.value.some(v => country.value === v.id)) || [];
    const selectedUsers = usersSelect?.filter((user: any) => rule.users.value.some(v => user.value === v.id)) || [];

    const setRule = (nextRules: any) => {
        setFieldValue(rulesPath, nextRules);
    }

    const handleSelectArray = (selected: any, field: string) => {
        const next = selected ? selected.map((o: any) => ({id: o.value})) : []
        setRule({...rule, [field]: {type: "array", value: next}});
    }

    const getByPath = (obj: any, path: string): any => {
        return path.split(".").reduce((acc: any, key: string) => {
            if (acc && acc[key] !== undefined) {
                return acc[key];
            }
            return undefined;
        }, obj);
    };

    const collectErrors = (node: any): string[] => {
        if (!node) return [];

        if (typeof node === "string") return [node];

        if (Array.isArray(node))
            return node.flatMap(item => collectErrors(item));

        if (typeof node === "object")
            return Object.values(node).flatMap(value => collectErrors(value));

        return [];
    };

    const renderError = (errors: any, touched: any, path: string) => {
        const isTouched = getByPath(touched, path);
        if (!isTouched) return null;

        const target = getByPath(errors, path);
        if (!target) return null;

        const messages = collectErrors(target);
        if (messages.length === 0) return null;

        return (
            <div className="text-danger font-size-12 mt-1">
                {messages.map((msg, i) => (
                    <div key={i}>{msg}</div>
                ))}
            </div>
        );
    };

    return (
        <>
            <div className="mb-2">
                <Label className="mb-1">Countries:</Label>
                <Select
                    isMulti
                    name="rule.countries.value"
                    placeholder="Select countries..."
                    options={countriesSelect}
                    value={selectedCountries}
                    isLoading={isLoadingCountries}
                    isDisabled={isLoadingCountries || isPendingSave}
                    onBlur={() => handleBlur("rule.countries")}
                    onChange={(selected: any) => handleSelectArray(selected, "countries")}/>

                {renderError(errors, touched, "rule.countries")}
            </div>

            <div className="mb-2">
                <Label className="mb-1">Users:</Label>
                <Select
                    isMulti
                    name="rule.users.value"
                    placeholder="Select users..."
                    menuPlacement="top"
                    options={usersSelect}
                    value={selectedUsers}
                    isLoading={isLoadingUsers}
                    isDisabled={isLoadingUsers || isPendingSave}
                    onBlur={() => handleBlur("rule.users")}
                    onChange={(selected: any) => handleSelectArray(selected, "users")}/>

                {renderError(errors, touched, "rule.users")}
            </div>
        </>
    );
}

export default AutoAssignOrderRule;